import _ from "lodash";
import React from "react";
import { useI18n } from "Hooks";
import { isSafari } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import classNames from "classnames";
import AnimateHeight from "UIComponents/AnimateHeight";
import classes from "./AIPanelContextSelection.scss";
import { PencilLineOutlined } from "@toddle-edu/ds-icons";

const AnimateHeightWrapper = props => {
  const { showContextList, children } = props;
  if (!isSafari()) return <AnimateHeight {...props} />;
  else return showContextList ? children : null;
};

const AIPanelContextItemLabel = ({ field }) => {
  if (_.isEmpty(field.label)) return null;

  const label = _.get(field, "label", "");
  const isRequired = _.get(field, "isRequired", false);

  return (
    <div className={classes.contextFieldItemLabel}>
      <span className="text-body-2">{label}</span>
      {isRequired && <span className={classes.requiredIndicator}>*</span>}
    </div>
  );
};

const AIPanelContextItemValue = ({ field, sendEvent = _.noop }) => {
  const { t } = useI18n();
  const value = _.get(field, "value", "");
  const isDisabled = _.get(field, "isDisabled", false);
  const displayValue = _.get(field, "displayValue", value);
  const selectedCount = _.get(field, "selectedCount", 0);
  const isEmpty = _.isEmpty(displayValue) && _.isEqual(selectedCount, 0);

  const containerClass = classNames("text-body-2", {
    [classes.contextFieldItemValue]: true,
    [classes.contextFieldItemEmpty]: isEmpty,
    [classes.contextFieldItemDisabled]: isDisabled,
  });

  const getDisplayText = () => {
    if (isEmpty) return t("common:select");
    if (!_.isEqual(selectedCount, 0)) {
      return `${selectedCount} ${t("common:selected")}`;
    }
    return displayValue;
  };

  const handleClick = () => {
    if (isDisabled) return;

    if (_.isFunction(field?.onClick)) {
      field?.onClick(_.omit(field, "onClick"));
    } else if (_.isFunction(sendEvent)) {
      sendEvent("CONTEXT_ITEM_CLICK", field);
    }
  };

  return (
    <div
      className={containerClass}
      onClick={handleClick}
      onKeyDown={e => onEnterOrSpaceKeyDown(e, handleClick)}
      role="button"
      tabIndex={0}
    >
      {getDisplayText()}

      {!isDisabled && (
        <PencilLineOutlined
          size={"xxx-small"}
          variant={"subtle"}
          className={classes.contextFieldItemValuePencilIcon}
        />
      )}
    </div>
  );
};

const renderTitle = ({ field }) => <AIPanelContextItemLabel field={field} />;

const renderValue = ({ field, onContextItemClick }) => (
  <AIPanelContextItemValue
    field={field}
    onContextItemClick={onContextItemClick}
  />
);

const AIPanelContextSelection = props => {
  const {
    sendEvent,
    contextFields,
    showContextList,
    renderMoreContextFields,
  } = props;

  return (
    <AnimateHeightWrapper
      duration={300}
      style={{ zIndex: 10 }}
      showContextList={showContextList}
      height={showContextList ? "auto" : 0}
      contentClassName={classes.aIPanelContextContent}
    >
      <div className={classes.aIPanelContextSelection}>
        {_.map(contextFields, (field, index) => (
          <div key={index} className={classes.contextFieldItem}>
            <AIPanelContextItemLabel field={field} />
            <AIPanelContextItemValue field={field} sendEvent={sendEvent} />
          </div>
        ))}
      </div>

      {renderMoreContextFields(renderTitle, renderValue)}
    </AnimateHeightWrapper>
  );
};

export default AIPanelContextSelection;
